import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, Breadcrumbs, FinalCTA } from "@/components/site/Layout";
import { HubSpotForm } from "@/components/site/HubSpotForm";
import { FAQSection, type FAQItem } from "@/components/site/FAQSection";
import { WebsiteCheck } from "@/components/site/WebsiteCheck";
import { SelfCheck } from "@/components/site/SelfCheck";
import { breadcrumbSchema, jsonLdScript, organizationSchema, ORG_LOGO, ORG_NAME, SITE_URL } from "@/lib/seo";

const TITLE = "Free Law Firm SEO Audit | Find What's Costing You Cases";
const DESCRIPTION =
  "Free SEO audit for law firms: a 30+ page report covering technical health, Google Maps, content and competitors, with a prioritized 90-day fix list.";
const URL = "https://pageonelawyers.com/law-firm-seo-audit";

const FAQS: FAQItem[] = [
  {
    q: "Is the law firm SEO audit really free?",
    a: "Yes. There is no charge and no obligation to buy anything. You receive the full 30+ page report and a 30-minute walk-through call. If you decide to work with us afterwards, that is your choice.",
  },
  {
    q: "How long does a law firm SEO audit take?",
    a: "You receive the completed report within 5–7 business days of submitting the form. The walk-through call is then booked at a time that suits your team.",
  },
  {
    q: "How do you audit a law firm website for SEO?",
    a: "A full audit checks technical health, on-page SEO, Google Business Profile, content quality, links and competitors. We crawl the site, review every practice page, compare your visibility with local competitors and turn the findings into a prioritized fix list.",
  },
  {
    q: "What's the best SEO audit for lawyers?",
    a: "The most useful audit for lawyers covers local search as well as technical SEO. It compares you with real competitors in your market and ranks fixes by impact. Generic website checkers miss legal-specific issues such as practice-page depth, attorney trust signals and advertising-rule risks.",
  },
  {
    q: "What do I need to provide for the audit?",
    a: "Only your website address, your main practice areas and the cities you serve. Access to Google Search Console or Analytics helps but is optional. We never need passwords to complete the audit.",
  },
  {
    q: "Will the audit include my competitors?",
    a: "Yes. We review up to 25 competitors for the searches that matter to your practice, including their keyword coverage, links and Google Maps visibility.",
  },
  {
    q: "Do you audit law firm websites in any US city?",
    a: "Yes. We audit law firm websites across the United States and tailor the local section to the city or region where you practice.",
  },
  {
    q: "What happens after the audit?",
    a: "We walk through the findings on a 30-minute call. You can fix the issues in-house, share the report with your current agency or ask us to help. There is no pressure either way.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Free Law Firm SEO Audit",
  serviceType: "SEO audit",
  provider: {
    "@type": "Organization",
    name: ORG_NAME,
    url: `${SITE_URL}/`,
    logo: ORG_LOGO,
  },
  areaServed: { "@type": "Country", name: "United States" },
  description: DESCRIPTION,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export const Route = createFileRoute("/law-firm-seo-audit")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      jsonLdScript(organizationSchema()),
      jsonLdScript(
        breadcrumbSchema([
          { label: "Home", path: "/" },
          { label: "Free SEO Audit", path: "/law-firm-seo-audit" },
        ]),
      ),
      jsonLdScript(serviceSchema),
    ],
  }),
  component: Audit,
});

const H2 = ({ children, id }: { children: React.ReactNode; id?: string }) => (
  <h2 id={id} className="max-w-3xl text-3xl font-semibold text-foreground sm:text-4xl">
    {children}
  </h2>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground">{children}</p>
);

function Audit() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-12 sm:px-6 sm:py-16 md:grid-cols-2 md:gap-12 md:py-20">
          <div>
            <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Free SEO Audit" }]} />
            <Eyebrow><span className="text-[var(--gold)]">Free SEO Audit</span></Eyebrow>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-semibold">Free Law Firm SEO Audit</h1>
            <p className="mt-4 text-xl text-primary-foreground/90">Find out exactly what's costing your firm cases.</p>
            <p className="mt-6 text-lg text-primary-foreground/80">Our free law firm SEO audit shows why your website isn't bringing in the cases it should. In 5–7 business days, a senior strategist delivers a 30+ page report on your technical health, content gaps, Google Maps visibility and competitors, plus the specific work needed to outrank them.</p>
            <ul className="mt-8 space-y-3 text-primary-foreground/85">
              {[
                "Technical SEO health (Core Web Vitals, crawl, schema)",
                "Top 25 competitor keyword & link gap",
                "Local pack & Google Business Profile review",
                "Content quality + EEAT scoring",
                "Prioritized 90-day fix list",
              ].map((x) => (
                <li key={x} className="flex gap-3"><span className="text-[var(--gold)]">✓</span>{x}</li>
              ))}
            </ul>
          </div>
          <div id="audit-form" className="min-w-0 scroll-mt-24 overflow-hidden rounded-xl bg-background p-4 text-foreground shadow-[var(--shadow-elegant)] sm:p-8">
            <h2 className="font-display text-2xl font-semibold">Request your free audit</h2>
            <HubSpotForm className="mt-4" />
            <p className="mt-3 text-xs text-muted-foreground">No spam. No sales calls without consent. Your data stays with us.</p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["1. Submit", "Tell us about your firm and current state."],
            ["2. We dig", "A senior strategist runs a full audit using enterprise SEO tooling."],
            ["3. Walk-through", "We present findings on a 30-minute call. No obligation."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-xl border border-border p-6 bg-card">
              <div className="font-display text-xl font-semibold text-[var(--gold-deep)]">{t}</div>
              <p className="mt-2 text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-muted-foreground">If the audit turns up crawl, speed or indexing problems, our <Link to="/services/technical-seo-for-law-firms" className="text-[var(--gold-deep)] underline underline-offset-4">technical SEO for law firms</Link> service can fix them for you.</p>
      </Section>

      <Section>
        <WebsiteCheck />
      </Section>

      <Section>
        <H2>What Our SEO Audit for Law Firms Covers</H2>
        <P>Most law firm websites lose cases for a handful of reasons that are easy to miss from the inside. Our SEO audit for law firms checks each one and ranks the problems by how much they cost you. Here is what the report reviews.</P>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div>
            <h3 className="text-xl font-semibold text-foreground">Technical SEO health</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">We crawl your site the way Google does. The report flags broken pages, redirect chains, slow load times, Core Web Vitals issues, indexing problems and missing or broken schema. This is the technical SEO audit attorneys usually never see, even though it decides whether Google can read your pages at all.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">On-page SEO and practice pages</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">We review titles, headings, internal links and the depth of each practice-area page. You get an on-page SEO audit checklist for your attorney site, showing which pages are thin, which compete with each other and which are missing.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Google Business Profile and local pack</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">For most firms, Google Maps brings more calls than any other result. We check your categories, service areas, reviews, photos and how your listing compares with the firms that appear above you.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Content quality and trust signals</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">Legal content has to be accurate, helpful and clearly written by or for qualified attorneys. We score your content for depth, freshness and trust signals, and note where a page needs attorney review or a stronger answer.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Links and competitors</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">We compare your links and keyword coverage with your top 25 competitors. This law firm website analysis shows the searches they win that you don't, and the links that help them rank.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">Your prioritized 90-day fix list</h3>
            <p className="mt-2 leading-relaxed text-muted-foreground">Every finding ends up in one plan, ordered by impact and effort. You can hand it to your web team, your current agency or us.</p>
          </div>
        </div>
      </Section>

      <Section>
        <H2>How the Free Law Firm SEO Audit Works</H2>
        <P>Requesting an audit takes two minutes. Fill in the form above with your website, main practice areas and the cities you serve. A senior strategist then reviews your site, your Google Business Profile and your competitors using professional SEO tools.</P>
        <P>Within 5–7 business days you receive the full report. We then walk you through it on a 30-minute call, explain what matters most and answer your questions. There is no cost and no obligation. If you want help fixing the issues, we can talk about that. If not, the report is yours to keep.</P>
      </Section>

      <Section>
        <H2>Who This Law Firm Website Audit Is For</H2>
        <P>The audit suits solo attorneys, small firms and multi-office practices. It is most useful if:</P>
        <ul className="mt-6 max-w-3xl list-disc space-y-3 pl-6 text-lg leading-relaxed text-muted-foreground">
          <li>Your website gets visits but few calls or form enquiries.</li>
          <li>Competitors with weaker reputations rank above you in Google or Maps.</li>
          <li>You have recently redesigned or moved your website and lost rankings.</li>
          <li>You pay an agency but can't see what the work is producing.</li>
          <li>You want a clear plan before choosing an SEO partner.</li>
        </ul>
      </Section>

      <Section>
        <SelfCheck />
      </Section>

      <Section>
        <H2>Law Firm SEO Audits for Specific Practice Areas</H2>
        <P>Search behaviour changes by practice. An SEO audit for family law firms looks closely at private, sensitive searches about custody and divorce, and at how clearly your pages explain the process. An SEO audit for divorce law firms also checks local reviews and the contact options clients use when they want discretion. Personal injury, criminal defense and estate planning audits focus on the searches and competitors specific to those areas. See our <Link to="/practice-areas/family-law-lawyer-seo" className="text-[var(--gold-deep)] underline underline-offset-4">family law SEO</Link> page for how we approach that practice in more depth.</P>
      </Section>

      <Section>
        <H2>What You Do With the Audit</H2>
        <P>The report is written for partners, not just marketers. Each issue explains what is wrong, why it matters for clients and enquiries, and what fixing it involves. Many firms use it to brief their current web team. Others use it to compare agencies. If you want us to handle the work, our <Link to="/services/law-firm-seo" className="text-[var(--gold-deep)] underline underline-offset-4">law firm SEO services</Link> page explains how ongoing engagements run.</P>
      </Section>

      <FAQSection
        items={FAQS}
        title="Law Firm SEO Audit: Questions Firms Ask"
        eyebrow="FAQ"
        addSchema
      />

      <FinalCTA
        title="Get your free law firm SEO audit"
        sub="Find out what's holding your website back and what to fix first. The report is free, detailed and yours to keep."
        auditLabel="Request my free audit"
        auditTo="#audit-form"
        strategyLabel=""
      />
    </Layout>
  );
}
